<script setup lang="ts">
import { computed, defineEmits, defineProps, watch } from 'vue'
import { isEmpty } from 'lodash'
import NrFormInput from '@nextrequestco/components/src/components/NrFormInput'
import NrSelect from '@nextrequestco/components/src/components/NrSelect'
import NrTooltip from '@nextrequestco/components/src/components/NrTooltip'
import NrIcon from '@nextrequestco/components/src/components/NrIcon'
import NrEditor from '@nextrequestco/components/src/components/NrEditor'
import NrDatepicker from '@nextrequestco/components/src/components/NrDatePicker'
import {
  FieldDefinition,
  FieldType,
  RadioSettings,
  RequestFieldValue,
  RichTextSettings,
  TextSettings,
  CheckboxSettings,
  DropdownSettings,
  RequestFieldOption,
  NumberSettings,
  DatepickerSettings
} from '../types/request_fields'
import striptags from 'striptags'

const props = defineProps<{
  definition: FieldDefinition
  modelValue:
    | RequestFieldValue
    | RequestFieldOption[]
    | RequestFieldOption
    | null
}>()

const emit = defineEmits<{
  (
    e: 'update:modelValue',
    fieldValue:
      | RequestFieldValue
      | RequestFieldOption[]
      | RequestFieldOption
      | null
  ): void
  (e: 'on-validate', fieldId: number, isValid: boolean): void
}>()

// This combines the definition and modelValue into one type so that code can know the type of the `modelValue` depending on the `field_type`.
type TypedProps =
  | {
      field_type: FieldType.Text // eslint-disable-line camelcase
      type_settings: TextSettings // eslint-disable-line camelcase
      modelValue: string
    }
  | {
      field_type: FieldType.Number // eslint-disable-line camelcase
      type_settings: NumberSettings // eslint-disable-line camelcase
      modelValue: string
    }
  | {
      field_type: FieldType.RichText // eslint-disable-line camelcase
      type_settings: RichTextSettings // eslint-disable-line camelcase
      modelValue: string
    }
  | {
      field_type: FieldType.Checkbox // eslint-disable-line camelcase
      type_settings: CheckboxSettings // eslint-disable-line camelcase
      modelValue: RequestFieldOption[]
    }
  | {
      field_type: FieldType.Radio // eslint-disable-line camelcase
      type_settings: RadioSettings // eslint-disable-line camelcase
      modelValue: RequestFieldOption | null
    }
  | {
      field_type: FieldType.Dropdown // eslint-disable-line camelcase
      type_settings: DropdownSettings // eslint-disable-line camelcase
      modelValue: RequestFieldOption[]
    }
  | {
      field_type: FieldType.Datepicker // eslint-disable-line camelcase
      type_settings: DatepickerSettings // eslint-disable-line camelcase
      modelValue: string
    }

const typedProps = computed<TypedProps>(() => {
  let value = props.modelValue

  switch (props.definition.field_type) {
    case FieldType.Text:
      value = value || ''
      if (typeof value !== 'string') {
        throw new Error('Unexpected type')
      }
      return {
        field_type: props.definition.field_type,
        modelValue: value || '',
        type_settings: {
          ...props.definition.type_settings,
          labelText: String(props.definition.type_settings.labelText)
        }
      }
    case FieldType.RichText:
      value = value || ''
      if (typeof value !== 'string') {
        throw new Error('Unexpected type')
      }
      return {
        field_type: props.definition.field_type,
        modelValue: value || '',
        type_settings: props.definition.type_settings
      }
    case FieldType.Number:
      value = value || ''
      if (typeof value !== 'string') {
        throw new Error('Unexpected type')
      }
      return {
        field_type: props.definition.field_type,
        modelValue: value,
        type_settings: props.definition.type_settings
      }
    case FieldType.Checkbox:
    case FieldType.Dropdown:
      value = value || []
      if (!Array.isArray(value)) {
        value = value ? [value] : []
      }
      return {
        field_type: props.definition.field_type,
        modelValue: value,
        type_settings: {
          ...props.definition.type_settings,
          labelText: String(props.definition.type_settings.labelText)
        }
      }
    case FieldType.Radio:
      if (Array.isArray(value)) {
        throw new Error('Unexpected type')
      }
      return {
        field_type: props.definition.field_type,
        modelValue: value as RequestFieldOption | null,
        type_settings: {
          ...props.definition.type_settings,
          labelText: String(props.definition.type_settings.labelText)
        }
      }
    case FieldType.Datepicker:
      value = value || ''
      if (typeof value !== 'string') {
        throw new Error('Unexpected type')
      }
      return {
        field_type: props.definition.field_type,
        modelValue: value || '',
        type_settings: props.definition.type_settings
      }
    default:
      throw new Error('Unexpected field type')
  }
})

const noLabel = computed(
  () =>
    props.definition.type_settings.hideLabel ||
    isEmpty(props.definition.type_settings.labelText)
)

const selectedDropdownValues = computed(() => {
  return mapOptionsToSelectFormat(typedProps.value.modelValue)
})

const minDate = computed(() => {
  if (!props.definition.type_settings.allowPastDays) {
    const today = new Date()
    today.setDate(today.getDate() - 1)
    return today.toISOString()
  }
  return undefined
})

const minMaxErrorText = computed(() => {
  if (typedProps.value.field_type !== FieldType.Text) {
    return ''
  }

  // eslint-disable-next-line camelcase
  const { type_settings, modelValue } = typedProps.value

  const strippedTextLength = striptags(modelValue).length

  // eslint-disable-next-line camelcase
  return strippedTextLength < type_settings.characterLimitMin
})

const minMaxValueErrorText = computed(() => {
  if (typedProps.value.field_type !== FieldType.Number) {
    return ''
  }

  // eslint-disable-next-line camelcase
  const { type_settings, modelValue } = typedProps.value

  if (!modelValue) {
    return ''
  }

  const number = Number(modelValue)
  let message = ''
  // eslint-disable-next-line camelcase
  if (type_settings.minValue && number < type_settings.minValue) {
    // eslint-disable-next-line camelcase
    message = `Minimum value of ${type_settings.minValue} required.`
    // eslint-disable-next-line camelcase
  } else if (type_settings.maxValue && number > type_settings.maxValue) {
    // eslint-disable-next-line camelcase
    message = `Maximum value of ${type_settings.maxValue} exceeded.`
    // eslint-disable-next-line camelcase
  } else if (!type_settings.allowDecimals && number % 1 !== 0) {
    message = 'Decimals are not allowed.'
  }

  return message
})

const isChecked = (
  option: RequestFieldOption,
  currentlyChecked: RequestFieldOption[] | RequestFieldOption | null
) => {
  if (Array.isArray(currentlyChecked)) {
    return currentlyChecked.some((checked) => checked.uuid === option.uuid)
  }
  return currentlyChecked?.uuid === option.uuid
}

const checkOption = (
  option: RequestFieldOption,
  checked: boolean,
  currentlyChecked: RequestFieldOption[] | RequestFieldOption | null
) => {
  if (
    props.definition.field_type === FieldType.Checkbox ||
    props.definition.field_type === FieldType.Dropdown
  ) {
    const alreadyChecked = isChecked(
      option,
      currentlyChecked as RequestFieldOption[]
    )
    if (alreadyChecked && !checked) {
      const selectedOptions = (currentlyChecked as RequestFieldOption[]).filter(
        (other) => other.uuid !== option.uuid
      )
      emit('update:modelValue', selectedOptions)
    } else if (!alreadyChecked && checked) {
      const selectedOptions = [
        option as RequestFieldOption,
        ...((currentlyChecked as RequestFieldOption[]) || [])
      ]
      emit('update:modelValue', selectedOptions)
    }
  } else if (props.definition.field_type === FieldType.Radio) {
    if (checked) {
      emit('update:modelValue', option)
    } else if (isChecked(option, currentlyChecked)) {
      emit('update:modelValue', null)
    }
  }
}

const isFieldValid = computed(() => {
  const {
    field_type: fieldType,
    modelValue,
    type_settings: typeSettings
  } = typedProps.value

  if (
    typedProps.value.field_type === FieldType.Checkbox ||
    typedProps.value.field_type === FieldType.Dropdown
  ) {
    return (
      !typedProps.value.type_settings.isMandatory ||
      (typedProps.value.modelValue && typedProps.value.modelValue.length > 0)
    )
  }

  if (fieldType === FieldType.Radio) {
    return !typeSettings.isMandatory || !!modelValue
  }

  if (fieldType === FieldType.RichText && typeof modelValue === 'string') {
    const min = typeSettings.characterLimitMin || 0
    const max = typeSettings.characterLimitMax || Infinity
    const textOnlyValue = striptags(modelValue)
    const length = textOnlyValue.length
    return length >= min && length <= max
  }

  if (fieldType === FieldType.Number) {
    return minMaxValueErrorText.value.length === 0
  }

  if (fieldType === FieldType.Datepicker) {
    return (
      !typedProps.value.type_settings.isMandatory ||
      (typedProps.value.modelValue && !isEmpty(typedProps.value.modelValue))
    )
  }

  return true
})

watch(
  isFieldValid,
  (val) => {
    if (
      [
        'checkbox',
        'radio',
        'datepicker',
        'number',
        'rich_text',
        'dropdown'
      ].includes(typedProps.value.field_type)
    ) {
      emit('on-validate', props.definition.id, val)
    }
  },
  { immediate: true }
)

const handleFormInputValidation = (isValid: boolean) => {
  const valid = isValid && isFieldValid.value
  emit('on-validate', props.definition.id, valid)
}

const handleDropdownChange = (value) => {
  const values = Array.isArray(value) ? value : value ? [value] : []
  const selectedUuids = values.map((v) => v.value)
  const selectedOptions: RequestFieldOption[] =
    typedProps.value.type_settings.options.filter((opt) =>
      selectedUuids.includes(opt.uuid)
    )

  emit('update:modelValue', selectedOptions)
}

function mapOptionsToSelectFormat(options: RequestFieldOption[]) {
  return options.map((opt) => ({
    label: opt.name,
    value: opt.uuid
  }))
}
</script>

<template>
  <NrFormInput
    v-if="typedProps.field_type === FieldType.Text"
    type="text"
    :modelValue="typedProps.modelValue"
    @update:modelValue="
      (fieldValue: string) => emit('update:modelValue', fieldValue)
    "
    :tag="typedProps.type_settings.isTextArea ? 'textarea' : 'input'"
    :label="typedProps.type_settings.labelText"
    :hideLabel="noLabel"
    :class="noLabel ? 'request-field-no-label' : null"
    :placeholder="typedProps.type_settings.placeholderText"
    :help="typedProps.type_settings.helperText"
    :required="typedProps.type_settings.isMandatory"
    :maxLength="typedProps.type_settings.characterLimitMax?.toString()"
    :minLength="typedProps.type_settings.characterLimitMin?.toString()"
    :withTooltip="!isEmpty(typedProps.type_settings.tooltipText)"
    :iconTooltipText="typedProps.type_settings.tooltipText"
    @on-validate="handleFormInputValidation"
  />

  <NrFormInput
    v-if="typedProps.field_type === FieldType.Number"
    type="number"
    :modelValue="typedProps.modelValue"
    @update:modelValue="
      (fieldValue: string) => {
        if (fieldValue === '') {
          emit('update:modelValue', null)
          return
        }
        typedProps.type_settings.allowDecimals
          ? emit('update:modelValue', fieldValue)
          : emit('update:modelValue', String(Math.floor(Number(fieldValue))))
      }
    "
    :label="typedProps.type_settings.labelText"
    :class="noLabel ? 'request-field-no-label' : null"
    :placeholder="typedProps.type_settings.placeholderText"
    :help="typedProps.type_settings.helperText"
    :required="typedProps.type_settings.isMandatory"
    :withTooltip="!isEmpty(typedProps.type_settings.tooltipText)"
    :iconTooltipText="typedProps.type_settings.tooltipText"
    :step="typedProps.type_settings.allowDecimals ? 'any' : '1'"
    @on-validate="handleFormInputValidation"
  />

  <p
    v-if="!isFieldValid && typedProps.field_type === FieldType.Number"
    class="help is-danger"
  >
    {{ minMaxValueErrorText }}
  </p>

  <NrEditor
    v-if="typedProps.field_type === FieldType.RichText"
    :modelValue="typedProps.modelValue"
    :placeholder="typedProps.type_settings.placeholderText"
    :required="typedProps.type_settings.isMandatory"
    :resizable="typedProps.type_settings.isExpandable"
    :maxLength="typedProps.type_settings.characterLimitMax?.toString()"
    :minLength="typedProps.type_settings.characterLimitMin?.toString()"
    :ordered-list="true"
    :bullet-list="true"
    :has-tooltip="!!typedProps.type_settings.tooltipText?.length"
    :tooltip="typedProps.type_settings.tooltipText"
    :label="
      typedProps.type_settings.labelText
        ? typedProps.type_settings.labelText
        : ''
    "
    @update:modelValue="(val: string) => emit('update:modelValue', val)"
  />

  <p
    v-if="
      !isFieldValid &&
      typedProps.field_type === FieldType.RichText &&
      (typedProps.type_settings.characterLimitMin ||
        typedProps.type_settings.characterLimitMax)
    "
    class="help is-danger"
  >
    {{
      minMaxErrorText
        ? `Minimum ${typedProps.type_settings.characterLimitMin} characters required.`
        : `Maximum ${typedProps.type_settings.characterLimitMax} characters exceeded.`
    }}
  </p>

  <fieldset
    v-if="typedProps.field_type === FieldType.Checkbox"
    class="checkbox-group"
  >
    <legend
      v-if="typedProps.type_settings.labelText"
      :class="noLabel ? 'is-sr-only' : 'checkbox-header'"
    >
      {{ typedProps.type_settings.labelText }}
      {{ typedProps.type_settings.isMandatory ? '*' : '' }}
    </legend>
    <div
      v-for="option in typedProps.type_settings.options"
      :key="option.uuid"
      class="checkbox-item"
    >
      <NrFormInput
        type="checkbox"
        :label="option.name"
        :modelValue="isChecked(option, typedProps.modelValue)"
        @update:modelValue="
          (checked: boolean) =>
            checkOption(option, checked, typedProps.modelValue)
        "
      />
    </div>
    <p
      v-if="!isFieldValid && typedProps.field_type === FieldType.Checkbox"
      class="help is-danger"
    >
      At least one option must be selected.
    </p>
  </fieldset>

  <fieldset
    v-if="typedProps.field_type === FieldType.Radio"
    class="radio-group"
  >
    <legend
      v-if="typedProps.type_settings.labelText"
      :class="noLabel ? 'is-sr-only' : 'radio-header'"
    >
      {{ typedProps.type_settings.labelText }}
      {{ typedProps.type_settings.isMandatory ? '*' : '' }}
    </legend>
    <div
      v-for="option in typedProps.type_settings.options"
      :key="option.uuid"
      class="radio-item"
    >
      <NrFormInput
        type="radio"
        :name="`radio-${props.definition.id}`"
        :label="option.name"
        :modelValue="isChecked(option, typedProps.modelValue)"
        @update:modelValue="
          (checked: boolean) =>
            checkOption(option, checked, typedProps.modelValue)
        "
      />
    </div>
    <p
      v-if="!isFieldValid && typedProps.field_type === FieldType.Radio"
      class="help is-danger"
    >
      An option must be selected.
    </p>
  </fieldset>

  <NrSelect
    v-if="typedProps.field_type === FieldType.Dropdown"
    :hideLabel="noLabel"
    :multiple="typedProps.type_settings.isMultiSelect"
    :placeholder="
      typedProps.type_settings.placeholderText || 'Select an option'
    "
    v-model="selectedDropdownValues"
    :options="mapOptionsToSelectFormat(typedProps.type_settings.options)"
    :required="typedProps.type_settings.isMandatory"
    @update:modelValue="handleDropdownChange"
    append-to-body
  >
    <template v-slot:label>
      <div class="dropdown-header-item">
        {{ typedProps.type_settings.labelText }}
        <NrTooltip
          v-if="typedProps.type_settings.tooltipText"
          :label="typedProps.type_settings.tooltipText"
          position="right"
        >
          <NrIcon
            class="inline-icon dropdown-tooltip-icon"
            name="info-circle"
          />
        </NrTooltip>
      </div>
    </template>
  </NrSelect>
  <div
    v-if="
      typedProps.field_type === FieldType.Dropdown &&
      typedProps.type_settings.helperText
    "
    class="dropdownHelper"
  >
    {{ typedProps.type_settings.helperText }}
  </div>

  <NrDatepicker
    v-if="typedProps.field_type === FieldType.Datepicker"
    :modelValue="typedProps.modelValue"
    @update:modelValue="
      (fieldValue: string) => emit('update:modelValue', fieldValue)
    "
    :minDate="minDate"
  >
    <template v-slot:label>
      <legend
        v-if="typedProps.type_settings.labelText"
        :class="noLabel ? 'is-sr-only' : 'checkbox-header'"
      >
        {{ typedProps.type_settings.labelText }}
        {{ typedProps.type_settings.isMandatory ? '*' : '' }}
      </legend>
    </template>
    <template v-slot:trigger>
      <NrIcon name="calendar-alt" />
    </template>
  </NrDatepicker>
  <p
    v-if="!isFieldValid && typedProps.field_type === FieldType.Datepicker"
    class="help is-danger"
  >
    A date must be selected.
  </p>
</template>

<style scoped lang="scss">
@import '@nextrequestco/components/src/styles/tokens/tokens.scss';

.checkbox-header,
.radio-header,
.dropdown-header {
  @include body-default-strong;
  color: $color-purple-110;
  line-height: calc($font-lineheight-3 + $border-width-medium);
}

.checkbox-group {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.radio-group {
  display: flex;
  flex-direction: column;
}

//added dropdown-header-item to set tooltip and icon in line
.checkbox-item,
.radio-item,
.dropdown-header-item {
  display: flex;
  align-items: center;
}

.dropdownHelper {
  font-size: small;
}

.dropdown-tooltip-icon {
  color: #0f1c70;
  margin-left: 8px;
  margin-right: 4px;
}

:deep(.vs__dropdown-option),
:deep(.vs__selected) {
  display: block !important;
  visibility: visible !important;
  max-width: none !important;
  white-space: normal !important;
}
</style>
